import { useEffect, useMemo } from "react";
import { fetchCostCenters, fetchEmployeeCostCenters } from "../../framework/redux/slices/costCenterSlice";
import { fetchMyVehicles } from "../../framework/redux/slices/vehicleSlice";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { IWorkTimeType } from "../../models/workShiftTimeSlot/workTimeType";
import {
    fetchWorkTimeTypes,
    fetchSalaryRowTypes,
} from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { IIdTitle } from "../../models/common/idTitle";
import { AsyncThunk } from "@reduxjs/toolkit";
import { VehicleListItemDto } from "../../models/vehicle/vehicle";
import { fetchTimeZones } from "../../framework/redux/slices/appSlice";
import { ISalaryRowType } from "../../models/salaryRowType/salaryRowType";
import { HourBookingCategory } from "../../models/workHours/workHoursItem";
import { Base } from "../../framework/common/base";
import { useTranslation } from "react-i18next";
import { IMuiSelectOption } from "../../components/framework/muiSelect";
import { getCustomers } from "../../framework/redux/slices/customerSlice";

export interface IWorkTimeFormOptions {
    customers?: IMuiSelectOption[];
    costCenters?: IMuiSelectOption[];
    allCostCenters?: IMuiSelectOption[];
    workTimeTypes?: IWorkTimeType[];
    vehicles?: IIdTitle[];
    myVehicles?: VehicleListItemDto[];
    salaryRowTypes?: ISalaryRowType[];
    timeZones?: IIdTitle[];
}

interface IWorkTimeFormOptionsMappingOption {
    method: AsyncThunk<void, void, any>;
    attr: string;
}

export const WorkTimeFormOptionsMapping = {
    customers: { method: getCustomers, attr: "customers" },
    costCenters: { method: fetchEmployeeCostCenters, attr: "costCenters" },
    workTimeTypes: { method: fetchWorkTimeTypes, attr: "workTimeTypes" },
    vehicles: { method: fetchMyVehicles, attr: "vehicles" },
    salaryRowTypes: {
        method: fetchSalaryRowTypes,
        attr: "salaryRowTypes",
    },
    timeZones: {
        method: fetchTimeZones,
        attr: "timeZones",
    },
    allCostCenters: { method: fetchCostCenters, attr: "allCostCenters" },
};

export const useWorkTimeFormOptions = (
    selectedFormOptions: string[],
    salaryRowTypeCategories: HourBookingCategory[]
): [IWorkTimeFormOptions, boolean] => {
    const workTimeTypes = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.workTimeTypes
    );
    const salaryRowTypes = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.salaryRowTypes
    );
    const myVehicles = useAppSelector(({ vehicle }) => vehicle.myVehicles);
    const myCostCenters = useAppSelector(({ costCenter }) => costCenter.employeeCostCenterItems);
    const timeZones = useAppSelector(({ app }) => app.timeZones);
    const customers = useAppSelector(({ customer }) => customer.customers);
    const allCostCenters = useAppSelector(({ costCenter }) => costCenter.costCenterItems);

    const { t } = useTranslation();

    const formOptions = useMemo(
        () => ({
            workTimeTypes,
            salaryRowTypes: salaryRowTypes?.filter((t) =>
                t.hourBookingCategories.some((c) =>
                    salaryRowTypeCategories.includes(parseInt(c))
                )
            ),
            vehicles: myVehicles?.map((vehicle) => ({
                id: vehicle.id,
                title: `${vehicle.registerNumber}, ${vehicle.brand}`,
            })),
            costCenters: myCostCenters?.map((costCenter) => ({
                id: costCenter.id,
                title: costCenter?.name,
                group: costCenter?.customerName ?? "", // Use empty string for sorting first.
            }))
            // Sort first so internal cost centers are first.
                .sort((a, b) => Base.stringCompare(a.group, b.group))
                .map((c) => ({
                    ...c,
                    // Add label for internal cost centers after sorting.
                    group: c.group || t("workTime.costCenterInternal"),
                })),
            allCostCenters: allCostCenters?.map((costCenter) => ({
                id: costCenter.id,
                title: costCenter?.name,
                group: costCenter?.customerName ?? "", // Use empty string for sorting first.
            }))
            // Sort first so internal cost centers are first.
                .sort((a, b) => Base.stringCompare(a.group, b.group))
                .map((c) => ({
                    ...c,
                    // Add label for internal cost centers after sorting.
                    group: c.group || t("workTime.costCenterInternal"),
                })),
            timeZones: timeZones?.map((tz) => ({
                id: tz.code,
                title: tz.title,
            })),
            customers: customers?.map((customer) => ({
                id: customer.id,
                title: customer?.name,
            }))
                .sort((a, b) => a.title.localeCompare(b.title)),
        }),
        [
            workTimeTypes,
            salaryRowTypes,
            myVehicles,
            myCostCenters,
            timeZones,
            salaryRowTypeCategories,
            customers,
            allCostCenters,
        ]
    );

    const ready = selectedFormOptions.every((o) => formOptions[o]);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const promises = selectedFormOptions.map((o) =>
            dispatch(
                (
                    WorkTimeFormOptionsMapping[
                        o
                    ] as IWorkTimeFormOptionsMappingOption
                ).method()
            )
        );

        return () => {
            promises.forEach((p) => p.abort());
        };
    }, []);

    return [
        {
            ...Object.fromEntries(
                selectedFormOptions.map((o) => [o, formOptions[o]])
            ),
            myVehicles,
        },
        ready,
    ];
};
